import { useState, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";

const API_URL = "https://ai-document-analyzer-fq1e.onrender.com";

const fontLink = document.createElement("link");
fontLink.rel = "stylesheet";
fontLink.href =
  "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600;700&family=DM+Sans:wght@300;400;500;600&display=swap";
document.head.appendChild(fontLink);

const globalStyle = document.createElement("style");
globalStyle.textContent = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { scroll-behavior: smooth; }
  body {
    font-family: 'DM Sans', sans-serif;
    background: #080c14;
    color: #e8e4d9;
    min-height: 100vh;
    overflow-x: hidden;
  }
  ::selection { background: rgba(212,175,96,0.35); }
  ::-webkit-scrollbar { width: 5px; }
  ::-webkit-scrollbar-track { background: #0d1120; }
  ::-webkit-scrollbar-thumb { background: #d4af60; border-radius: 10px; }
`;
document.head.appendChild(globalStyle);

const C = {
  bg: "#080c14",
  surface: "rgba(255,255,255,0.04)",
  border: "rgba(255,255,255,0.08)",
  borderHover: "rgba(212,175,96,0.45)",
  gold: "#d4af60",
  goldLight: "#e8c97a",
  goldDim: "rgba(212,175,96,0.18)",
  text: "#e8e4d9",
  muted: "rgba(232,228,217,0.5)",
  success: "#6fcf97",
  error: "#eb5757",
  glow: "rgba(212,175,96,0.12)",
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.55,
      delay: i * 0.08,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  }),
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.92 },
  show: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.45, ease: [0.34, 1.56, 0.64, 1] },
  },
};

function MeshBackground() {
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 0, overflow: "hidden", pointerEvents: "none" }}>
      <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse 80% 60% at 50% -10%, #111a2e 0%, #080c14 55%)" }} />
      <div style={{ position: "absolute", top: "-15%", right: "-10%", width: "55vw", height: "55vw", borderRadius: "50%", background: "radial-gradient(circle, rgba(212,175,96,0.07) 0%, transparent 70%)", filter: "blur(60px)" }} />
      <div style={{ position: "absolute", bottom: "-20%", left: "-15%", width: "60vw", height: "60vw", borderRadius: "50%", background: "radial-gradient(circle, rgba(70,90,180,0.08) 0%, transparent 70%)", filter: "blur(80px)" }} />
      <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(rgba(255,255,255,0.018) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.018) 1px, transparent 1px)", backgroundSize: "60px 60px" }} />
    </div>
  );
}

function GlassCard({ children, style = {}, hover = true }) {
  const [hovered, setHovered] = useState(false);
  return (
    <motion.div
      onHoverStart={() => hover && setHovered(true)}
      onHoverEnd={() => hover && setHovered(false)}
      animate={{
        borderColor: hovered ? C.borderHover : C.border,
        boxShadow: hovered
          ? `0 8px 40px rgba(0,0,0,0.5), 0 0 0 1px ${C.borderHover}, inset 0 1px 0 rgba(255,255,255,0.06)`
          : "0 4px 24px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.04)",
      }}
      transition={{ duration: 0.25 }}
      style={{ background: "rgba(255,255,255,0.038)", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)", border: `1px solid ${C.border}`, borderRadius: 20, ...style }}
    >
      {children}
    </motion.div>
  );
}

function AccentLine({ width = 48 }) {
  return <div style={{ width, height: 2, background: `linear-gradient(90deg, ${C.gold}, transparent)`, borderRadius: 2, margin: "12px 0" }} />;
}

function Spinner() {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 20 }}>
      <div style={{ position: "relative", width: 64, height: 64 }}>
        <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1.4, ease: "linear" }} style={{ position: "absolute", inset: 0, borderRadius: "50%", border: `2px solid ${C.goldDim}`, borderTopColor: C.gold }} />
        <motion.div animate={{ rotate: -360 }} transition={{ repeat: Infinity, duration: 2.2, ease: "linear" }} style={{ position: "absolute", inset: 10, borderRadius: "50%", border: `1.5px solid transparent`, borderTopColor: C.goldLight, opacity: 0.6 }} />
        <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: 10, height: 10, borderRadius: "50%", background: C.gold, filter: `blur(3px) drop-shadow(0 0 6px ${C.gold})` }} />
      </div>
      <motion.p animate={{ opacity: [0.4, 1, 0.4] }} transition={{ repeat: Infinity, duration: 2 }} style={{ fontFamily: "'DM Sans'", fontSize: 13, letterSpacing: "0.18em", textTransform: "uppercase", color: C.muted }}>
        Analyzing Document
      </motion.p>
    </div>
  );
}

function ResultField({ label, value, icon, index }) {
  const sentimentColor =
    label === "Sentiment"
      ? value?.toLowerCase().includes("pos") ? C.success
      : value?.toLowerCase().includes("neg") ? C.error
      : C.gold
      : null;

  return (
    <motion.div
      custom={index}
      variants={fadeUp}
      initial="hidden"
      animate="show"
      style={{ padding: "18px 22px", borderRadius: 14, background: "rgba(255,255,255,0.03)", border: `1px solid ${C.border}` }}
      whileHover={{ background: "rgba(212,175,96,0.06)", borderColor: C.goldDim }}
    >
      <p style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.18em", textTransform: "uppercase", color: C.muted, marginBottom: 7, display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ fontSize: 13 }}>{icon}</span>{label}
      </p>
      <p style={{ fontSize: 15, fontWeight: 500, color: sentimentColor || C.text, lineHeight: 1.5, wordBreak: "break-word" }}>
        {value || <span style={{ color: C.muted, fontStyle: "italic", fontWeight: 400 }}>Not available</span>}
      </p>
    </motion.div>
  );
}

export default function App() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const inputRef = useRef();

  const acceptFile = useCallback((f) => {
    if (!f) return;
    setFile(f);
    setResult(null);
    setError(null);
    if (f.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(f);
    } else {
      setPreview(null);
    }
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) acceptFile(dropped);
  }, [acceptFile]);

  const onDragOver = (e) => { e.preventDefault(); setDragging(true); };
  const onDragLeave = () => setDragging(false);
  const onInputChange = (e) => acceptFile(e.target.files?.[0]);

  // ── Upload ─────────────────────────────────────────────────────────────────
  const handleUpload = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const ext = file.name.split(".").pop().toLowerCase();
      const fileType =
        ext === "pdf" ? "pdf"
        : ext === "docx" || ext === "doc" ? "docx"
        : "image";

      const res = await axios.post(
        `${API_URL}/api/document-analyze`,
        {
          fileName: file.name,
          fileType: fileType,
          fileBase64: base64,
        },
        {
          headers: {
            "Content-Type": "application/json",
            "x-api-key": "sk_track2_987654321",
          },
        }
      );

      const data = res.data;

      setResult({
        name: data.entities?.names?.[0] || file.name,
        summary: data.summary || "",
        total_amount: data.entities?.amounts?.[0] || "",
        date: data.entities?.dates?.[0] || "",
        sentiment: data.sentiment || "",
        organizations: data.entities?.organizations?.join(", ") || "",
      });

    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err.message ||
        "Something went wrong. Please try again.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    setError(null);
    setLoading(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const fileIcon = file
    ? file.type.startsWith("image/") ? "🖼️"
    : file.type === "application/pdf" ? "📄"
    : file.name.endsWith(".docx") || file.name.endsWith(".doc") ? "📝"
    : "📎"
    : null;

  const resultFields = result
    ? [
        { label: "Document Name",  value: result.name,          icon: "📄" },
        { label: "Summary",        value: result.summary,       icon: "📋" },
        { label: "Organizations",  value: result.organizations, icon: "🏢" },
        { label: "Total Amount",   value: result.total_amount,  icon: "💰" },
        { label: "Date",           value: result.date,          icon: "📅" },
        { label: "Sentiment",      value: result.sentiment,     icon: "🎭" },
      ]
    : [];

  return (
    <div style={{ position: "relative", minHeight: "100vh", zIndex: 1 }}>
      <MeshBackground />

      <div style={{ position: "relative", zIndex: 1, maxWidth: 780, margin: "0 auto", padding: "clamp(32px, 6vw, 72px) clamp(16px, 5vw, 32px) 80px" }}>

        <motion.header initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} style={{ textAlign: "center", marginBottom: "clamp(40px, 8vw, 72px)" }}>
          <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.15, duration: 0.5 }}
            style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 16px", borderRadius: 100, background: C.goldDim, border: `1px solid rgba(212,175,96,0.3)`, marginBottom: 22 }}>
            <span style={{ fontSize: 10 }}>✦</span>
            <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.18em", textTransform: "uppercase", color: C.gold }}>AI-Powered</span>
            <span style={{ fontSize: 10 }}>✦</span>
          </motion.div>

          <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(36px, 7vw, 68px)", fontWeight: 600, lineHeight: 1.1, letterSpacing: "-0.02em", color: C.text }}>
            Document{" "}
            <span style={{ background: `linear-gradient(135deg, ${C.gold} 0%, ${C.goldLight} 50%, ${C.gold} 100%)`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              Analyzer
            </span>
          </h1>

          <motion.div style={{ display: "flex", justifyContent: "center" }}><AccentLine width={56} /></motion.div>

          <p style={{ fontSize: "clamp(13px, 2vw, 15px)", color: C.muted, maxWidth: 440, margin: "0 auto", lineHeight: 1.75, fontWeight: 300 }}>
            Upload any document and let our intelligence engine extract key insights, summaries, and metadata in seconds.
          </p>
        </motion.header>

        <motion.div custom={0} variants={fadeUp} initial="hidden" animate="show">
          <GlassCard style={{ overflow: "hidden" }}>
            <div onDrop={onDrop} onDragOver={onDragOver} onDragLeave={onDragLeave} onClick={() => !file && inputRef.current?.click()}
              style={{ padding: "clamp(28px, 5vw, 48px)", cursor: file ? "default" : "pointer" }}>
              <AnimatePresence mode="wait">
                {!file ? (
                  <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, y: -10 }} style={{ textAlign: "center" }}>
                    <motion.div animate={dragging ? { scale: 1.12, rotate: [0, -4, 4, 0] } : { scale: 1 }} transition={{ duration: 0.35 }}
                      style={{ width: 72, height: 72, borderRadius: 20, background: C.goldDim, border: `1px solid rgba(212,175,96,0.3)`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, margin: "0 auto 20px" }}>
                      {dragging ? "📂" : "📁"}
                    </motion.div>
                    <motion.div animate={{ borderColor: dragging ? C.gold : "rgba(212,175,96,0.2)", background: dragging ? "rgba(212,175,96,0.05)" : "transparent" }} transition={{ duration: 0.2 }}
                      style={{ border: `1.5px dashed rgba(212,175,96,0.2)`, borderRadius: 14, padding: "20px 24px" }}>
                      <p style={{ fontSize: 16, fontWeight: 500, color: C.text, marginBottom: 6 }}>{dragging ? "Release to upload" : "Drop your file here"}</p>
                      <p style={{ fontSize: 12, color: C.muted }}>or <span style={{ color: C.gold, textDecoration: "underline" }}>browse files</span> — PDF, DOC, DOCX, Images</p>
                    </motion.div>
                    <input ref={inputRef} type="file" accept=".pdf,.doc,.docx,image/*" style={{ display: "none" }} onChange={onInputChange} />
                  </motion.div>
                ) : (
                  <motion.div key="file" variants={scaleIn} initial="hidden" animate="show">
                    <AnimatePresence>
                      {preview && (
                        <motion.div initial={{ opacity: 0, scale: 0.95, height: 0 }} animate={{ opacity: 1, scale: 1, height: "auto" }} exit={{ opacity: 0, scale: 0.95, height: 0 }}
                          style={{ marginBottom: 20, borderRadius: 12, overflow: "hidden", border: `1px solid ${C.border}` }}>
                          <img src={preview} alt="Preview" style={{ width: "100%", maxHeight: 280, objectFit: "contain", background: "rgba(0,0,0,0.3)", display: "block" }} />
                        </motion.div>
                      )}
                    </AnimatePresence>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 14, flex: 1, minWidth: 0 }}>
                        <div style={{ width: 48, height: 48, borderRadius: 12, background: C.goldDim, border: `1px solid rgba(212,175,96,0.3)`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>
                          {fileIcon}
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <p style={{ fontSize: 14, fontWeight: 500, color: C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{file.name}</p>
                          <p style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{(file.size / 1024).toFixed(1)} KB · {file.type || "Unknown type"}</p>
                        </div>
                      </div>
                      <motion.button whileHover={{ scale: 1.08, background: "rgba(235,87,87,0.12)" }} whileTap={{ scale: 0.94 }} onClick={handleReset}
                        style={{ background: "rgba(235,87,87,0.07)", border: `1px solid rgba(235,87,87,0.2)`, borderRadius: 10, padding: "8px 14px", color: "#eb5757", fontSize: 12, fontWeight: 500, cursor: "pointer", display: "flex", alignItems: "center", gap: 5, whiteSpace: "nowrap" }}>
                        ✕ Remove
                      </motion.button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </GlassCard>
        </motion.div>

        <AnimatePresence>
          {file && !result && (
            <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.35 }}
              style={{ marginTop: 20, display: "flex", justifyContent: "center" }}>
              <motion.button
                whileHover={!loading ? { scale: 1.03, boxShadow: `0 0 36px rgba(212,175,96,0.35), 0 8px 28px rgba(0,0,0,0.4)` } : {}}
                whileTap={!loading ? { scale: 0.97 } : {}}
                onClick={handleUpload}
                disabled={loading}
                style={{ position: "relative", padding: "16px 52px", borderRadius: 100, background: loading ? "rgba(212,175,96,0.25)" : `linear-gradient(135deg, ${C.gold} 0%, #c9a43a 100%)`, border: "none", color: loading ? C.muted : "#0a0c12", fontSize: 14, fontWeight: 600, letterSpacing: "0.06em", cursor: loading ? "not-allowed" : "pointer", overflow: "hidden", boxShadow: `0 4px 20px rgba(212,175,96,0.25)` }}>
                {loading ? (
                  <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <motion.span animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: "linear" }} style={{ display: "inline-block" }}>⟳</motion.span>
                    Processing…
                  </span>
                ) : (
                  <span style={{ display: "flex", alignItems: "center", gap: 8 }}>✦ Analyze Document</span>
                )}
                {!loading && (
                  <motion.div initial={{ x: "-100%", opacity: 0 }} whileHover={{ x: "200%", opacity: [0, 0.4, 0] }} transition={{ duration: 0.65 }}
                    style={{ position: "absolute", inset: 0, background: "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)", pointerEvents: "none" }} />
                )}
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {loading && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.4 }} style={{ marginTop: 24 }}>
              <GlassCard hover={false} style={{ padding: "52px 32px", textAlign: "center" }}>
                <Spinner />
                <p style={{ marginTop: 24, fontSize: 12, color: C.muted, lineHeight: 1.7, maxWidth: 280, margin: "20px auto 0" }}>
                  Our AI is reading, parsing, and extracting intelligence from your document…
                </p>
              </GlassCard>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {error && (
            <motion.div variants={scaleIn} initial="hidden" animate="show" exit={{ opacity: 0 }}
              style={{ marginTop: 20, padding: "16px 20px", borderRadius: 14, background: "rgba(235,87,87,0.07)", border: `1px solid rgba(235,87,87,0.25)`, display: "flex", alignItems: "flex-start", gap: 12 }}>
              <span style={{ fontSize: 18, lineHeight: 1 }}>⚠️</span>
              <div>
                <p style={{ fontSize: 13, fontWeight: 600, color: "#eb5757", marginBottom: 3 }}>Analysis Failed</p>
                <p style={{ fontSize: 12, color: "rgba(235,87,87,0.75)", lineHeight: 1.5 }}>{error}</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {result && (
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }} style={{ marginTop: 28 }}>
              <GlassCard hover={false} style={{ padding: "clamp(24px, 4vw, 40px)" }}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 28, flexWrap: "wrap", gap: 12 }}>
                  <div>
                    <div style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "4px 12px", borderRadius: 100, background: "rgba(111,207,151,0.1)", border: "1px solid rgba(111,207,151,0.25)", marginBottom: 10 }}>
                      <span style={{ width: 6, height: 6, borderRadius: "50%", background: C.success, boxShadow: `0 0 8px ${C.success}` }} />
                      <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.15em", textTransform: "uppercase", color: C.success }}>Analysis Complete</span>
                    </div>
                    <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(22px, 4vw, 30px)", fontWeight: 600, color: C.text, lineHeight: 1.2 }}>Extracted Intelligence</h2>
                    <AccentLine width={40} />
                  </div>
                  <motion.button whileHover={{ scale: 1.04, background: C.goldDim }} whileTap={{ scale: 0.96 }} onClick={handleReset}
                    style={{ padding: "9px 18px", borderRadius: 10, background: "rgba(255,255,255,0.04)", border: `1px solid ${C.border}`, color: C.muted, fontSize: 12, fontWeight: 500, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                    ↩ New File
                  </motion.button>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
                  {resultFields.map((f, i) => <ResultField key={f.label} {...f} index={i} />)}
                </div>

                <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
                  style={{ marginTop: 24, fontSize: 11, color: "rgba(232,228,217,0.3)", textAlign: "center", letterSpacing: "0.05em" }}>
                  ✦ Powered by AI Document Intelligence · Results may vary by document quality
                </motion.p>
              </GlassCard>
            </motion.div>
          )}
        </AnimatePresence>

        <motion.footer initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }} style={{ marginTop: 60, textAlign: "center" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 8 }}>
            <div style={{ flex: 1, height: 1, background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.07))" }} />
            <span style={{ fontSize: 12, color: C.gold, letterSpacing: "0.1em" }}>✦</span>
            <div style={{ flex: 1, height: 1, background: "linear-gradient(270deg, transparent, rgba(255,255,255,0.07))" }} />
          </div>
          <p style={{ fontSize: 11, color: "rgba(232,228,217,0.25)", letterSpacing: "0.08em" }}>AI Document Analyzer · Secure & Private</p>
        </motion.footer>

      </div>
    </div>
  );
}
